import { useSettingsStore as useCoreSettingsStore } from '@flows.cash/core'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

export const useSettingsStore = () => {
  const { t } = useTranslation()
  const coreStore = useCoreSettingsStore()

  // Helper function to handle errors
  const handleError = (error: any, defaultMessage: string) => {
    console.error('Settings error:', error)
    
    // Check if it's an RLS policy violation
    if (error?.message?.includes('Permission denied') || 
        error?.code === '42501' || 
        (typeof error === 'object' && error?.message?.includes('row-level security policy'))) {
      toast.error(t('settings.permissionError'), {
        description: t('settings.permissionErrorDescription'),
        action: {
          label: t('common.learnMore'),
          onClick: () => window.open('https://supabase.com/docs/guides/auth/row-level-security', '_blank')
        }
      })
    } else {
      // Generic error message
      toast.error(defaultMessage)
    }
  }

  return {
    ...coreStore,
    updateSettings: async (userId: string, data: Parameters<typeof coreStore.updateSettings>[1]) => {
      try {
        await coreStore.updateSettings(userId, data)
        toast.success(t('settings.updateSuccess'))
      } catch (error) {
        handleError(error, t('settings.updateError'))
        throw error
      }
    },

    updateTransactionColumns: async (userId: string, columns: Parameters<typeof coreStore.updateTransactionColumns>[1]) => {
      try {
        await coreStore.updateTransactionColumns(userId, columns)
        toast.success(t('settings.columnsUpdateSuccess'))
      } catch (error) {
        handleError(error, t('settings.columnsUpdateError'))
        throw error
      }
    },

    updateColumnOrder: async (userId: string, order: Parameters<typeof coreStore.updateColumnOrder>[1]) => {
      try {
        await coreStore.updateColumnOrder(userId, order)
        toast.success(t('settings.orderUpdateSuccess'))
      } catch (error) {
        handleError(error, t('settings.orderUpdateError'))
        throw error
      }
    },

    updateDateTimeFormat: async (userId: string, format: Parameters<typeof coreStore.updateDateTimeFormat>[1]) => {
      try {
        await coreStore.updateDateTimeFormat(userId, format)
        toast.success(t('settings.formatUpdateSuccess'))
      } catch (error) {
        handleError(error, t('settings.formatUpdateError'))
        throw error
      }
    }
  }
}
